import React, { useEffect, useState } from "react";

import { AlertTriangle, ArrowLeft, BarChart3, Check, Info } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { useGetDocumentLogsQuery } from "../api";
import { useGetMerchantRecordQuery } from "../api/merchantRecords";
import Loader from "../components/Loader";
import { stepLabel } from "../components/ProcessingLogs";
import { monthRangeLabel } from "../utils/merchantRecord";
import { formatCurrency } from "../utils/utils";

import type {
	MerchantRecord,
	MerchantRecordAnalysis,
	MerchantRecordLine,
	MerchantRecordMonth,
} from "../api/merchantRecords";

const POLL_MS = 10_000;
const COUNT_WORD = ["", "One", "Two", "Three"];

// v9 classes (.pill, .btn, .btn-sm), written once so every use matches.
const PILL =
	"inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-1 text-xs font-medium leading-[1.3]";
const PILL_TONE = {
	success: "border-green-200 bg-green-100 text-green-800",
	neutral: "border-gray-200 bg-gray-100 text-gray-600",
	warning: "border-sand-300 bg-sand-100 text-sand-900",
	primary: "border-primary-100 bg-primary-50 text-primary-700",
	error: "border-red-100 bg-red-50 text-red-700",
};
const BTN =
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border px-4 py-2 text-sm font-medium leading-5";
const BTN_SECONDARY = `${BTN} border-gray-300 bg-white text-primary-700 hover:bg-gray-50`;
const BTN_PRIMARY = `${BTN} border-transparent bg-primary-500 text-white hover:bg-primary-600`;
const SM = "!rounded-md !px-3 !py-1 !text-xs !leading-[18px]";

const SECTIONS: [string, string][] = [
	["dues_fees_assessments", "Dues, fees & assessments"],
	["interchange", "Interchange"],
	["auth_discount", "Authorization & discount fees"],
	["downgrades", "Downgrades"],
	["other_services", "Other fees"],
];

const isRunning = (status: string) =>
	status === "processing" || status === "uploaded";

const monthPill = (month: MerchantRecordMonth) => {
	if (isRunning(month.status))
		return (
			<span className={`${PILL} ${PILL_TONE.primary}`}>
				<i className="inline-block h-2.5 w-2.5 animate-spin rounded-full border-2 border-primary-200 border-t-primary-400" />
				Processing
			</span>
		);
	if (month.status === "completed" && month.duplicate_month)
		return (
			<span className={`${PILL} ${PILL_TONE.neutral}`}>
				Same month again · not averaged
			</span>
		);
	if (month.status === "completed" && month.blocks_quote)
		return (
			<span className={`${PILL} ${PILL_TONE.warning}`}>
				Quote blocked · not averaged
			</span>
		);
	if (month.status === "completed")
		return month.needs_review ? (
			<span className={`${PILL} ${PILL_TONE.warning}`}>Needs a person</span>
		) : (
			<span className={`${PILL} ${PILL_TONE.success}`}>Completed</span>
		);
	return <span className={`${PILL} ${PILL_TONE.error}`}>Failed</span>;
};

const clock = (seconds: number) =>
	`${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, "0")}`;
const number = (n: number) =>
	n.toLocaleString("en-US", { maximumFractionDigits: 0 });
const percent = (n: number) => `${n.toFixed(2)}%`;
const dash = (value: number, format: (n: number) => string) =>
	value ? format(value) : "—";

const MonthCard: React.FC<{ month: MerchantRecordMonth }> = ({ month }) => {
	const { data } = useGetDocumentLogsQuery(month.file_id, {
		pollingInterval: isRunning(month.status) ? POLL_MS : 0,
	});
	const steps = [...(data?.logs ?? [])]
		.filter((log) => log.step_name !== "platform_progress")
		.sort(
			(a, b) =>
				new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
		);
	return (
		<div className="rounded-lg border border-gray-200 bg-white">
			<div className="flex items-center justify-between gap-4 px-6 pt-6">
				<div className="min-w-0">
					<div className="text-lg leading-normal text-gray-900">
						{month.statement_month || "Month not read yet"}
					</div>
					<div className="mt-0.5 truncate text-sm text-gray-500">
						{month.filename}
					</div>
				</div>
				{monthPill(month)}
			</div>
			<div className="p-6">
				<ul>
					{steps.map((log, i) => {
						const failed = log.status === "failed";
						return (
							<li
								key={log.id}
								className={`flex items-center gap-3 py-2 text-sm ${i ? "border-t border-gray-100" : ""}`}
							>
								<span
									className={`flex h-5 w-5 flex-none items-center justify-center rounded-full text-[11px] font-semibold ${failed ? "bg-sand-300 text-sand-900" : "bg-green-100 text-green-800"}`}
								>
									{failed ? "!" : "✓"}
								</span>
								{stepLabel(log.step_name)}
								<span className="ml-auto text-xs tabular-nums text-gray-500">
									{log.duration_seconds ? clock(log.duration_seconds) : ""}
								</span>
							</li>
						);
					})}
				</ul>
				<Link
					to={`/analysis/${month.file_id}`}
					className="mt-4 inline-block text-sm font-medium text-primary-700"
				>
					Open this month
				</Link>
			</div>
		</div>
	);
};

const SumCard: React.FC<{
	tone: "current" | "proposed" | "savings";
	label: string;
	v1: string;
	l1: React.ReactNode;
	v2: string;
	l2: string;
}> = ({ tone, label, v1, l1, v2, l2 }) => {
	const t = {
		current: {
			card: "from-gray-100 to-gray-200 border-gray-300",
			k: "text-gray-600",
			bar: "bg-red-500",
			v: "text-gray-900",
			l: "text-gray-500",
		},
		proposed: {
			card: "from-blue-50 to-blue-100 border-blue-200",
			k: "text-blue-700",
			bar: "bg-blue-500",
			v: "text-gray-900",
			l: "text-blue-700",
		},
		savings: {
			card: "from-green-50 to-green-100 border-green-200",
			k: "text-green-700",
			bar: "bg-green-400",
			v: "text-green-900",
			l: "text-green-700",
		},
	}[tone];
	return (
		<div
			className={`relative grid grid-cols-2 items-end gap-x-4 gap-y-0.5 overflow-hidden rounded-xl border bg-gradient-to-br px-4 py-3 ${t.card}`}
		>
			<span className="pointer-events-none absolute -right-12 -top-12 h-24 w-24 rounded-full bg-white/35" />
			<div
				className={`col-span-2 mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[.12em] ${t.k}`}
			>
				<i className={`h-3 w-1 rounded-sm ${t.bar}`} />
				{label}
			</div>
			<div className={`text-[22px] font-bold leading-[1.1] ${t.v}`}>{v1}</div>
			<div className={`text-[22px] font-bold leading-[1.1] ${t.v}`}>{v2}</div>
			<div
				className={`mt-1 whitespace-nowrap text-xs font-medium uppercase tracking-[.08em] ${t.l}`}
			>
				{l1}
			</div>
			<div
				className={`mt-1 whitespace-nowrap text-xs font-medium uppercase tracking-[.08em] ${t.l}`}
			>
				{l2}
			</div>
		</div>
	);
};

// v9 .fee table colours: fixed violet/indigo tints in both themes, not palette tokens.
const CUR = "bg-[#f5f3ff]";
const NEW = "bg-[#eef2ff]";
const TH = "h-10 px-3 text-sm font-medium text-black";
const TD =
	"h-10 whitespace-nowrap border-b border-gray-100 px-3 text-left text-xs tabular-nums text-black";

const FeeTable: React.FC<{
	title: string;
	lines: MerchantRecordLine[];
	period: string;
	monthsAveraged: number;
}> = ({ title, lines, period, monthsAveraged }) => {
	const total = (key: "current_fee" | "proposed_fee") =>
		lines.filter((l) => l.is_root).reduce((sum, l) => sum + l[key], 0);
	const side = (line: MerchantRecordLine, fee: number, tint: string) => (
		<>
			<td className={`${TD} ${tint} w-16 pl-7`}>
				{dash(line.transactions, number)}
			</td>
			<td className={`${TD} ${tint}`}>—</td>
			<td className={`${TD} ${tint}`}>—</td>
			<td className={`${TD} ${tint}`}>{dash(line.volume, formatCurrency)}</td>
			<td className={`${TD} ${tint} pr-7 text-right`}>{formatCurrency(fee)}</td>
		</>
	);
	const sub = (tint: string) =>
		["#", "Rate", "Fee", "Volume", "Fee due"].map((h, i) => (
			<th
				key={h}
				className={`${TH} ${tint} text-left ${i === 0 ? "w-16 pl-7" : ""} ${i === 4 ? "pr-7 text-right" : ""}`}
			>
				{h}
			</th>
		));
	return (
		<>
			<div className="px-7 py-4 text-base font-semibold uppercase text-black">
				{title}
			</div>
			<div className="overflow-x-auto">
				<table className="w-full border-collapse">
					<thead>
						<tr>
							<th className="h-10 w-[400px] bg-black/5 pl-7 text-left text-xs font-semibold text-black">
								Processing month: average of {period}
							</th>
							<th
								colSpan={5}
								className={`${TH} bg-[linear-gradient(rgba(0,0,0,.05),rgba(0,0,0,.05)),#f5f3ff] text-center`}
							>
								Current processor
							</th>
							<th
								colSpan={5}
								className={`${TH} bg-[linear-gradient(rgba(0,0,0,.05),rgba(0,0,0,.05)),#eef2ff] text-center`}
							>
								With Basys
							</th>
						</tr>
						<tr>
							<th className="border-b border-black/[.04] bg-white" />
							{sub(CUR)}
							{sub(NEW)}
						</tr>
					</thead>
					<tbody>
						{lines.map((line) => (
							<tr key={line.key}>
								<td
									className={`${TD} whitespace-normal text-sm ${line.is_root ? "pl-7" : "pl-11"}`}
								>
									{line.description}
									{line.months_present < monthsAveraged && (
										<span className="ml-2 inline-block rounded border border-sand-300 bg-sand-100 px-1.5 py-px align-middle text-[10px] font-semibold text-sand-900">
											in {line.months_present} of {monthsAveraged} months
										</span>
									)}
								</td>
								{side(line, line.current_fee, CUR)}
								{side(line, line.proposed_fee, NEW)}
							</tr>
						))}
						<tr className="font-semibold">
							<td className={`${TD} bg-black/5 pl-7 text-xs`}>Section total</td>
							<td colSpan={4} className={`${TD} ${CUR}`} />
							<td
								className={`${TD} ${CUR} pr-7 text-right text-sm font-medium`}
							>
								{formatCurrency(total("current_fee"))}
							</td>
							<td colSpan={4} className={`${TD} ${NEW}`} />
							<td
								className={`${TD} ${NEW} pr-7 text-right text-sm font-medium`}
							>
								{formatCurrency(total("proposed_fee"))}
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</>
	);
};

const StatusRow: React.FC<{
	icon: React.ReactNode;
	spinning?: boolean;
	warning?: boolean;
	onClick?: () => void;
	children: React.ReactNode;
	right?: React.ReactNode;
}> = ({ icon, spinning, warning, onClick, children, right }) => (
	<div
		onClick={onClick}
		className={`flex items-center justify-between gap-4 border-b px-6 py-4 ${warning ? "border-sand-300 bg-[#fff4e6]" : "border-gray-200 bg-white"} ${onClick ? "cursor-pointer" : ""}`}
	>
		<div className="flex items-center gap-3">
			<span
				className={`flex h-9 w-9 flex-none items-center justify-center rounded-full ${
					spinning
						? "animate-spin border-[3px] border-primary-100 border-t-primary-400 text-transparent"
						: warning
							? "bg-sand-300 text-sand-900"
							: "bg-primary-100 text-gray-900"
				}`}
			>
				{icon}
			</span>
			<div>{children}</div>
		</div>
		{right}
	</div>
);

// The record's monthly fees are the mean of each month's priced totals (the same numbers
// as the cards). Section totals can add up to more: a plan may not charge a section.
const MonthlyFeesRow: React.FC<{ analysis: MerchantRecordAnalysis }> = ({
	analysis,
}) => {
	const rootSum = (key: "current_fee" | "proposed_fee") =>
		analysis.lines.filter((l) => l.is_root).reduce((sum, l) => sum + l[key], 0);
	const { current_fees, proposed_fees } = analysis.totals;
	const differs =
		Math.abs(rootSum("current_fee") - current_fees) >= 0.01 ||
		Math.abs(rootSum("proposed_fee") - proposed_fees) >= 0.01;
	return (
		<div className="overflow-x-auto pt-4">
			<table className="w-full border-collapse">
				<tbody>
					<tr className="font-semibold" data-testid="monthly-fees-row">
						<td className={`${TD} w-[400px] bg-black/5 pl-7 text-sm`}>
							Monthly fees
						</td>
						<td className={`${TD} ${CUR} pr-7 text-right text-sm font-medium`}>
							{formatCurrency(current_fees)}
						</td>
						<td className={`${TD} ${NEW} pr-7 text-right text-sm font-medium`}>
							{formatCurrency(proposed_fees)}
						</td>
					</tr>
				</tbody>
			</table>
			{differs && (
				<p className="px-7 pt-2 text-xs text-gray-500">
					Each month is priced by its own plan. Fees a plan does not charge (for
					example pass-through interchange on a flat rate) are in the section
					totals but not in the monthly fees.
				</p>
			)}
		</div>
	);
};

export const MerchantRecordView: React.FC<{ record: MerchantRecord }> = ({
	record,
}) => {
	const [logOpen, setLogOpen] = useState(false);
	const n = record.months.length;
	const period = monthRangeLabel(
		record.months.flatMap((m) =>
			m.statement_month ? [m.statement_month] : [],
		),
	);
	const analysis = record.analysis;
	const totals = analysis?.totals;
	const rate = (fees: number) =>
		totals && totals.volume ? percent((fees / totals.volume) * 100) : "—";
	const money = (v: number | undefined) =>
		v === undefined ? "—" : formatCurrency(v);
	const bps =
		totals && totals.volume
			? Math.round(
					((totals.current_fees - totals.proposed_fees) / totals.volume) *
						10000,
				)
			: null;
	const savingsPct =
		totals && totals.current_fees
			? Math.round((totals.savings / totals.current_fees) * 100)
			: null;
	const review = record.months.filter((m) => m.needs_review);
	const label = (m: MerchantRecordMonth) => m.statement_month || m.filename;
	const duplicates = record.months.filter((m) => m.duplicate_month);
	const blocked = record.months.filter((m) => m.blocks_quote);
	const notes = [
		duplicates.length > 0 &&
			`${duplicates.map(label).join(", ")} was uploaded twice. Only the latest upload is averaged.`,
		blocked.length > 0 &&
			`${blocked.map(label).join(", ")} cannot be quoted (not a statement or missing pages), so it is not in the average.`,
		record.mixed_rate_plan_types &&
			"The months were read as different statement types. Check that they are the same merchant account.",
	].filter((note): note is string => Boolean(note));
	const failed = record.months.filter((m) => m.status === "failed").length;
	const processing = record.status === "processing";
	const word = (count: number) => COUNT_WORD[count] ?? String(count);
	const title = {
		processing: `Processing ${word(n).toLowerCase()} statements`,
		completed: "Statement processing completed",
		partial: `Statement processing completed · ${word(failed).toLowerCase()} did not finish`,
		failed: "Statement processing failed",
	}[record.status];

	return (
		<div className="flex min-h-0 flex-col">
			<div className="flex items-center justify-between gap-4 border-b border-gray-200 bg-gray-50 px-6 py-4">
				<div className="flex min-w-0 flex-auto items-center gap-4">
					<Link to="/" className={`${BTN_SECONDARY} ${SM} !py-1.5`}>
						<ArrowLeft className="h-3 w-3" />
						Back
					</Link>
					<div className="min-w-0">
						<div className="break-words text-lg font-semibold text-gray-900">
							{record.merchant_name}
							{period && ` · ${period}`}
						</div>
						<div className="mt-2 flex items-center gap-3">
							<span className={`${PILL} ${PILL_TONE.neutral}`}>
								{n} months grouped
							</span>
						</div>
					</div>
				</div>
			</div>

			<div className="grid grid-cols-1 gap-4 border-b border-gray-200 bg-gray-50 px-6 py-4 md:grid-cols-3">
				<SumCard
					tone="current"
					label="Current processor"
					v1={totals ? rate(totals.current_fees) : "—"}
					l1="Rate"
					v2={money(totals?.current_fees)}
					l2="Monthly fees"
				/>
				<SumCard
					tone="proposed"
					label="With Basys"
					v1={totals ? rate(totals.proposed_fees) : "—"}
					l1={bps === null ? "Rate" : `Rate · ${bps} bps lower`}
					v2={money(totals?.proposed_fees)}
					l2="Monthly fees"
				/>
				<SumCard
					tone="savings"
					label="Customer savings"
					v1={money(totals?.savings)}
					l1={
						savingsPct === null ? "Monthly" : `Monthly (${savingsPct}% lower)`
					}
					v2={money(totals?.yearly_savings)}
					l2="Yearly"
				/>
			</div>

			<StatusRow
				icon={<Check className="h-5 w-5" />}
				spinning={processing}
				onClick={() => setLogOpen((v) => !v)}
				right={
					<span className="font-medium text-green-500">
						Target: under 15 min for {n} months · aiming for under 10
					</span>
				}
			>
				<div className="text-base font-medium text-gray-900">{title}</div>
				<div className="text-sm text-gray-500">
					{word(n)} statements ran in parallel and became one merchant record ·{" "}
					<button
						type="button"
						className="text-primary-700"
						onClick={(e) => {
							e.stopPropagation();
							setLogOpen((v) => !v);
						}}
					>
						{logOpen ? "Hide processing log" : "Show processing log"}
					</button>
				</div>
			</StatusRow>
			{logOpen && (
				<div className="border-b border-gray-200 bg-gray-50 p-6">
					<div className="grid grid-cols-1 gap-6 md:grid-cols-3">
						{record.months.map((month) => (
							<MonthCard key={month.file_id} month={month} />
						))}
					</div>
					{totals && (
						<div className="mt-4 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
							<Info className="mt-px h-5 w-5 flex-none text-gray-500" />
							<div>
								The analysis shows the{" "}
								{word(analysis.months_averaged).toLowerCase()}
								-month average: {formatCurrency(totals.volume)} volume,{" "}
								{number(totals.transactions)} transactions,{" "}
								{number(totals.batches)} batches.
							</div>
						</div>
					)}
				</div>
			)}

			{notes.length > 0 && (
				<StatusRow warning icon={<AlertTriangle className="h-5 w-5" />}>
					<div className="text-base font-medium text-gray-900">
						Check the months
					</div>
					{notes.map((note) => (
						<div key={note} className="text-sm text-gray-500">
							{note}
						</div>
					))}
				</StatusRow>
			)}

			{review.length > 0 && (
				<StatusRow
					warning
					icon={<AlertTriangle className="h-5 w-5" />}
					right={
						<div className="flex items-center gap-3">
							<button
								type="button"
								className={`${BTN_SECONDARY} ${SM}`}
								onClick={() => setLogOpen(true)}
							>
								Show the log
							</button>
							<Link
								to={`/analysis/${review[0].file_id}`}
								className={`${BTN_PRIMARY} ${SM}`}
							>
								Open {review[0].statement_month || "the month"}
							</Link>
						</div>
					}
				>
					<div className="text-base font-medium text-gray-900">
						Person review required
					</div>
					<div className="text-sm text-gray-500">
						{review.map((m) => m.statement_month || m.filename).join(", ")}{" "}
						{review.length === 1 ? "needs" : "need"} a person. Confirm the
						flagged lines on that month before the quote goes out.
					</div>
				</StatusRow>
			)}

			<StatusRow icon={<BarChart3 className="h-5 w-5" />}>
				<div className="flex items-center gap-2">
					<div className="text-base font-medium text-gray-900">
						Fee Analysis
					</div>
					{analysis ? (
						<span className={`${PILL} ${PILL_TONE.success}`}>
							Analysis Ready
						</span>
					) : (
						<span className={`${PILL} ${PILL_TONE.neutral}`}>
							Shows when every month has finished
						</span>
					)}
				</div>
			</StatusRow>

			{analysis && (
				<div className="bg-white pb-16">
					{[
						...SECTIONS,
						...[...new Set(analysis.lines.map((l) => l.section))]
							.filter((s) => !SECTIONS.some(([key]) => key === s))
							.map((s): [string, string] => [s, s.replace(/_/g, " ")]),
					].map(([key, label]) => {
						const lines = analysis.lines.filter((l) => l.section === key);
						return lines.length ? (
							<FeeTable
								key={key}
								title={label}
								lines={lines}
								period={period}
								monthsAveraged={analysis.months_averaged}
							/>
						) : null;
					})}
					<MonthlyFeesRow analysis={analysis} />
				</div>
			)}
		</div>
	);
};

const MerchantRecordPage: React.FC = () => {
	const { recordId = "" } = useParams<{ recordId: string }>();
	const [polling, setPolling] = useState(true);
	const { data: record, error } = useGetMerchantRecordQuery(recordId, {
		pollingInterval: polling ? POLL_MS : 0,
	});
	useEffect(() => {
		if (record) setPolling(record.status === "processing");
	}, [record]);

	if (error) {
		return (
			<div className="mx-auto max-w-[1440px] px-8 pb-16 pt-6 text-sm text-gray-600">
				This merchant record was not found.{" "}
				<Link to="/" className="text-primary-700">
					Back to statements
				</Link>
			</div>
		);
	}
	if (!record) return <Loader />;
	return <MerchantRecordView record={record} />;
};

export default MerchantRecordPage;
